import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  Checkbox,
  FormGroup,
  FormControlLabel,
  TextField,
  Divider,
  Card,
  CardMedia,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Favorite as FavoriteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  CheckCircle as CheckCircleIcon,
  ShoppingCart as ShoppingCartIcon
} from '@mui/icons-material';
import { MenuItem } from '../data/menuData';

interface ItemCustomizationModalProps {
  item: MenuItem | null;
  open: boolean;
  onClose: () => void;
  onAddToCart: (item: MenuItem, customizations: any, quantity: number) => void;
}

const ItemCustomizationModal: React.FC<ItemCustomizationModalProps> = ({
  item,
  open,
  onClose,
  onAddToCart
}) => {
  const [quantity, setQuantity] = useState(1);
  const [removedToppings, setRemovedToppings] = useState<string[]>([]);
  const [extraProtein, setExtraProtein] = useState<string | null>(null);
  const [specialRequests, setSpecialRequests] = useState('');
  const [showAddedToCart, setShowAddedToCart] = useState(false);

  const handleToppingRemoval = (topping: string) => {
    setRemovedToppings(prev => 
      prev.includes(topping)
        ? prev.filter(t => t !== topping)
        : [...prev, topping]
    );
  };

  const handleAddToCart = () => {
    if (!item) return;
    
    const customizations = {
      removedToppings,
      extraProtein,
      specialRequests,
      quantity
    };
    
    onAddToCart(item, customizations, quantity);
    setShowAddedToCart(true);
    
    // Close modal after showing animation
    setTimeout(() => {
      onClose();
      setShowAddedToCart(false);
    }, 1500);
    
    // Reset state
    setQuantity(1);
    setRemovedToppings([]);
    setExtraProtein(null);
    setSpecialRequests('');
  };

  const calculatePrice = () => {
    if (!item) return 0;
    let basePrice = item.price;
    if (extraProtein) basePrice += 2.99;
    return basePrice * quantity;
  };

  const toppingsToRemove = [
    'Mexican Rice',
    'Refried Beans', 
    'Lettuce',
    'Pico de Gallo',
    'Avocado Salsa',
    'Cotija Cheese',
    'Cilantro'
  ];

  const proteinOptions = [
    { name: 'Birria', price: 2.99 },
    { name: 'Steak', price: 2.99 },
    { name: 'Chicken', price: 2.99 }
  ];

  const goesWellWith = [
    {
      name: 'Pico De Gallo and Chips',
      price: 7.99,
      image: 'https://photos.tryotter.com/cdn-cgi/image/fit=crop,width=256,quality=80/menu-photos/969ede5f-9f8b-4e72-b2c9-a22c6971d321.jpeg',
      description: 'A Fresh, Zesty Mix of Diced Tomatoes, Onions, Cilantro, and Lime'
    },
    {
      name: 'Jalapeño Taki Queso and Chips',
      price: 6.99,
      image: 'https://photos.tryotter.com/cdn-cgi/image/fit=crop,width=640,quality=80/menu-photos/688e3ed2-5f99-4904-a658-15462794b67f.jpeg',
      description: 'A Rich, Creamy Melted Cheese Dip, Perfectly Seasoned'
    }
  ];

  if (!item) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.6)'
        },
        '& .MuiDialog-paper': {
          margin: { xs: 2, md: 4 },
          maxHeight: '90vh',
          borderRadius: 3,
          backgroundColor: 'background.paper',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden'
        }
      }}
    >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
        {/* Modern Header with Image */}
        <Box sx={{ position: 'relative' }}>
          {/* Hero Image */}
          <Box sx={{ 
            position: 'relative', 
            height: { xs: 200, md: 250 },
            background: `linear-gradient(135deg, rgba(0,0,0,0.3), rgba(0,0,0,0.1)), url(${item.image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            alignItems: 'flex-end',
            borderRadius: 0
          }}>
            {/* Close Button */}
            <IconButton 
              onClick={onClose}
              sx={{ 
                position: 'absolute',
                top: 16,
                right: 16,
                backgroundColor: 'rgba(0,0,0,0.6)',
                color: 'white',
                '&:hover': { 
                  backgroundColor: 'rgba(0,0,0,0.8)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
            
            {/* Title Overlay */}
            <Box sx={{ 
              p: 3, 
              background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.7) 100%)',
              width: '100%'
            }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}
              >
                {item.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <FavoriteIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.8)' }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  {item.likes} likes
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Scrollable Content */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>

          {/* Content */}
          <Box sx={{ p: { xs: 3, md: 4 } }}>
            {/* Item Description */}
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="body1" 
                color="text.primary" 
                sx={{ 
                  fontSize: '1.1rem',
                  lineHeight: 1.6,
                  fontWeight: 400
                }}
              >
                {item.description}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Topping Removal */}
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600, 
                  mb: 3,
                  fontSize: { xs: '1.1rem', md: '1.25rem' }
                }}
              >
                Customize Your {item.name.includes('Bowl') ? 'Bowl' : 'Order'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Remove any ingredients you don't want
              </Typography>
              
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                gap: 1 
              }}>
                {toppingsToRemove.map((topping) => (
                  <FormControlLabel
                    key={topping}
                    control={
                      <Checkbox
                        checked={removedToppings.includes(topping)}
                        onChange={() => handleToppingRemoval(topping)}
                        sx={{ 
                          p: 1.5,
                          '& .MuiSvgIcon-root': { fontSize: 20 }
                        }}
                      />
                    }
                    label={`No ${topping}`}
                    sx={{ 
                      m: 0,
                      p: 1,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: removedToppings.includes(topping) ? '#424242' : 'divider',
                      backgroundColor: removedToppings.includes(topping) ? 'rgba(66, 66, 66, 0.08)' : 'transparent',
                      '&:hover': {
                        borderColor: '#424242',
                        backgroundColor: 'rgba(66, 66, 66, 0.04)'
                      },
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.95rem',
                        fontWeight: 500
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Extra Protein */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Would You Like Extra {item.name.includes('Bowl') ? 'Rice Bowl' : ''} Protein?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Select up to 1 option
              </Typography>
              
              <FormGroup>
                {proteinOptions.map((protein) => (
                  <FormControlLabel
                    key={protein.name}
                    control={
                      <Checkbox
                        checked={extraProtein === protein.name}
                        onChange={(e) => setExtraProtein(e.target.checked ? protein.name : null)}
                        sx={{ p: 2 }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        <span>{protein.name}</span>
                        <Typography variant="body2" color="text.primary">
                          +${protein.price.toFixed(2)}
                        </Typography>
                      </Box>
                    }
                    sx={{ 
                      display: 'flex',
                      py: 1,
                      '& .MuiFormControlLabel-label': {
                        flex: 1
                      }
                    }}
                  />
                ))}
              </FormGroup>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Special Requests */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Special Requests
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                We'll try our best to accommodate requests, but can't make changes that affect pricing.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Add special request"
                value={specialRequests}
                onChange={(e) => setSpecialRequests(e.target.value)}
                variant="outlined"
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Goes Well With */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Goes Well With
              </Typography>
              
              {goesWellWith.map((suggestion) => (
                <Card 
                  key={suggestion.name}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    p: 2, 
                    mb: 2,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  <Box sx={{ 
                    width: 32, 
                    height: 32, 
                    borderRadius: '50%', 
                    backgroundColor: '#424242',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <AddIcon sx={{ color: 'white', fontSize: 16 }} />
                  </Box>
                  
                  <CardMedia
                    component="img"
                    image={suggestion.image}
                    alt={suggestion.name}
                    sx={{ width: 64, height: 64, borderRadius: 1 }}
                  />
                  
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {suggestion.name}
                      </Typography>
                      <Typography variant="body2">
                        ${suggestion.price.toFixed(2)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {suggestion.description}
                    </Typography>
                  </Box>
                </Card>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Modern Bottom Bar */}
        <Box sx={{ 
          borderTop: '1px solid', 
          borderColor: 'divider', 
          p: 3, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 3,
          backgroundColor: 'background.paper'
        }}>
          {/* Quantity Selector */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            border: '1px solid', 
            borderColor: 'divider', 
            borderRadius: 2,
            backgroundColor: 'background.default',
            minWidth: 120,
            height: 48
          }}>
            <IconButton 
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              disabled={quantity <= 1}
              sx={{ 
                borderRadius: 0,
                width: 40,
                height: 40,
                '&:disabled': { opacity: 0.3 }
              }}
            >
              <RemoveIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Typography sx={{ 
              flex: 1,
              textAlign: 'center',
              fontWeight: 600,
              fontSize: '1rem'
            }}>
              {quantity}
            </Typography>
            <IconButton 
              onClick={() => setQuantity(quantity + 1)}
              sx={{ 
                borderRadius: 0,
                width: 40,
                height: 40
              }}
            >
              <AddIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Add to Cart Button */}
          <Button
            variant="contained"
            fullWidth
            onClick={handleAddToCart}
            disabled={showAddedToCart}
            sx={{ 
              py: 1.5,
              px: 3,
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              textTransform: 'none',
              height: 48,
              transition: 'all 0.2s ease',
              '&:active': {
                transform: 'scale(0.98)'
              },
              '&:disabled': {
                backgroundColor: '#4caf50',
                color: 'white',
                opacity: 0.9
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {showAddedToCart ? (
                <>
                  <CheckCircleIcon sx={{ fontSize: 18 }} />
                  <span>Added!</span>
                </>
              ) : (
                <ShoppingCartIcon sx={{ fontSize: 20 }} />
              )}
            </Box>
            <Typography sx={{ fontWeight: 700 }}>
              ${calculatePrice().toFixed(2)}
            </Typography>
          </Button>
        </Box>
      </DialogContent>


    </Dialog>
  );
};

export default ItemCustomizationModal;
